<template>
  <div class="white_box">
    <div
      class="title"
      @click="$parent.showAddCompetitor = !$parent.showAddCompetitor"
    >
      <h3 class="heading">
        {{ canEditCompetitors ? 'Add competitor' : 'Added Competitors' }}
      </h3>
      <button :class="'arrow_btn purple '+(open ? 'open' : '')" />
    </div>
    <accordion-transition>
      <div
        v-if="open"
        class="add_wrapper"
      >
        <div class="help_button">
          <button
            v-if="suggestionsAvailable && canEditCompetitors"
            class="help"
            @click="showSuggestions"
          >
            Need help?
          </button>
        </div>
        <div
          v-if="canEditCompetitors"
          class="add_form"
        >
          <input
            v-model="competitor_url"
            autocomplete="off"
            class="add_input"
            placeholder="https://www.website.com"
            type="text"
          >
          <button
            :disabled="loading"
            class="btn primary_btn btn_md"
            @click="add(competitor_url)"
          >
            <i
              v-if="loading"
              class="fas fa-spinner fa-spin"
            />
            {{ loading ? 'Adding Competitor' : 'Add Competitor' }}
          </button>
          <small
            v-if="error"
            class="text-danger text-center"
          >{{ error }}</small>
        </div>

        <div class="sub_heading_wrapper">
          <div
            v-if="visibleCompetitors.length > 0"
            class="sub_heading"
          >
            {{ canEditCompetitors ? 'Previous added competitors' : '' }}
          </div>
          <div
            v-if="competitorFilter"
            class="clear_filter"
            @click="clearFilter('competitorFilter')"
          >
            Clear filter <span
              class="delete"
            />
          </div>
        </div>

        <div
          v-if="competitors.length > 0"
          class="list_items"
        >
          <div
            v-for="(competitor, index) in visibleCompetitors"
            :key="index"
            :class="'list_item competitor '+(competitor.simple_url === competitorFilter ? 'active' : '')"
          >
            <span
              class="keyword_value"
              @click="setFilter({key: 'competitorFilter', value: competitor.simple_url})"
            >{{
              competitor.simple_url
            }}</span>
          </div>
        </div>

        <div
          v-else
          class="no_competitors"
        >
          No competitors added
        </div>

        <div
          v-if="competitors.length > visibleTags"
          :class="'show '+(showAll ? 'less' : 'more')"
          @click="showAll = !showAll"
        >
          <i class="arrow_btn purple" />
          {{ showAll ? 'Show Less' : 'Show More' }}
        </div>
      </div>
    </accordion-transition>
  </div>

  <suggested-competitors v-if="showSuggestedCompetitorsModal" />
  <serp-suggested-competitors v-if="showSerpSuggestedCompetitorsModal" />
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex';
import AccordionTransition from './../../../../reports/components/global/transitions/AccordionTransition.vue';
import SuggestedCompetitors from './../modals/SuggestedCompetitorsModal.vue';
import SerpSuggestedCompetitors from './../modals/SerpSuggestedCompetitorsModal';
import Swal from 'sweetalert2';

export default {
  components: {
    AccordionTransition,
    SuggestedCompetitors,
    SerpSuggestedCompetitors
  },
  props: {
    open: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showAll: false,
      loading: false,
      competitor_url: '',
      error: false,
      visibleTags: 3
    }
  },
  computed: {
    ...mapState('competition', ['competitors']),
    ...mapState('keywordIdeas', ['competitorFilter']),
    ...mapGetters('competition', [
      'suggestedCompetitors',
      'serpSuggestedCompetitors',
      'showSuggestedCompetitorsModal',
      'showSerpSuggestedCompetitorsModal',
    ]),
    ...mapGetters('user', [
      'canEditCompetitors'
    ]),
    suggestionsAvailable(){
      return (this.suggestedCompetitors.length > 0 || this.serpSuggestedCompetitors.length > 0);
    },
    visibleCompetitors() {
      if (this.showAll) {
        return this.competitors;
      } else {
        return this.competitors.slice(0, this.visibleTags);
      }

    }
  },
  watch: {
    competitor_url() {
      this.error = false;
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    ...mapActions('competition', [
        'fetchData',
        'addCompetitor',
        'openSuggestedCompetitorsModal',
        'openSerpSuggestedCompetitorsModal',
    ]),
    ...mapActions('keywordIdeas', [
        'fetchSuggestions',
        'setFilter',
        'clearFilter',
    ]),
    add() {
      this.loading = true;
      this.addCompetitor(this.competitor_url)
        .then((response) => {
          this.competitor_url = '';
          this.loading = false;
          this.fetchSuggestions(true).then(() => {
            this.setFilter({key: 'competitorFilter', value: response.simple_url});
          });
        })
        .catch(response => {
          this.loading = false;
          const error = response.data;
          if (response.status === 422) {
            this.error = error.message;
            return;
          }

          let {title, message} = error;
          if (response.status >= 500) {
            title = title ?? 'Whoops!';
            message = message ?? 'Something went wrong. Please try again.';
          }

          Swal.fire({
            icon: 'warning',
            title: title,
            html: message,
          });
        })
        .finally(() => {
          this.fetchData();
        });
    },
    filter(competitor) {
      return competitor.simple_url.toLowerCase().includes(this.competitorFilter.toLowerCase());
    },
    showSuggestions() {
      if (this.suggestedCompetitors.length > 0) {
        this.openSuggestedCompetitorsModal();
      } else if (this.serpSuggestedCompetitors.length > 0) {
        this.openSerpSuggestedCompetitorsModal();
      }
    }
  },
}
</script>
<style scoped>
.add_form .btn {
  background-color: var(--secondary-color);
}

.add_form .btn:hover {
  background-color: var(--secondary-color-dark);
}

.sub_heading {
  font-size: 0.875rem;
  line-height: 1.72;
  letter-spacing: -0.00715em;
  color: var(--secondary-font-color);
  font-weight: 700;
}

.help {
  padding-top: 0;
  padding-bottom: 0;
  margin-left: auto;
  margin-bottom: 5px;
  font-size: 0.75rem;
  line-height: 1.334;
  letter-spacing: 0.00834em;
  font-weight: 400;
  color: var(--secondary-color);
  cursor: pointer;
}

.no_competitors {
  margin-top: 1rem;
  text-align: center;
  color: var(--primary-font-color);
}
</style>
